import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface AttendanceContextType {
  // Trigger to refresh calendar data
  refreshTrigger: number;
  
  // Function to trigger calendar refresh
  triggerCalendarRefresh: () => void;
  
  // Function to trigger stats refresh
  triggerStatsRefresh: () => void;
  
  // Last attendance action timestamp
  lastActionTimestamp: number;
  
  // Update last action timestamp
  updateLastAction: () => void;
}

const AttendanceContext = createContext<AttendanceContextType | undefined>(undefined);

interface AttendanceProviderProps {
  children: ReactNode;
}

export const AttendanceProvider: React.FC<AttendanceProviderProps> = ({ children }) => {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [lastActionTimestamp, setLastActionTimestamp] = useState(Date.now());

  const triggerCalendarRefresh = useCallback(() => {
    console.log('🔄 Triggering calendar refresh...');
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const triggerStatsRefresh = useCallback(() => {
    console.log('📊 Triggering stats refresh...');
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const updateLastAction = useCallback(() => {
    const timestamp = Date.now();
    console.log('⏰ Updating last action timestamp:', new Date(timestamp).toLocaleTimeString());
    setLastActionTimestamp(timestamp);
    // Also trigger refresh
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const value: AttendanceContextType = {
    refreshTrigger,
    triggerCalendarRefresh,
    triggerStatsRefresh,
    lastActionTimestamp,
    updateLastAction,
  };

  return (
    <AttendanceContext.Provider value={value}>
      {children}
    </AttendanceContext.Provider>
  );
};

export const useAttendanceContext = (): AttendanceContextType => {
  const context = useContext(AttendanceContext);
  if (!context) {
    throw new Error('useAttendanceContext must be used within an AttendanceProvider');
  }
  return context;
};
